import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  Cancel as CancelIcon,
  AddLink as AddLinkIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { API_URL } from '../../../utils/config';
import { NoteIcon } from '../../../components/accounting/NoteIcon';

interface Bill {
  id: string;
  docNumber?: string;
  transactionDate?: string;
  dueDate?: string;
  totalAmount?: number;
  balance?: number;
  currencyCode?: string;
  privateNote?: string;
  subsidiaryInvoiceId?: number;
  subsidiaryInvoiceNumber?: string;
  hasNotes?: boolean;
  createdAt?: string;
  updatedAt?: string;
  invoice?: {
    id?: string;
    docNumber?: string;
    transactionDate?: string;
    customerId?: string;
    customerName?: string;
    totalAmount?: number;
    balance?: number;
    currencyCode?: string;
    currencyName?: string;
    status?: string;
    source?: string;
    hasNotes?: boolean;
  } | null;
  mapping?: {
    reason?: string;
    notes?: string;
  } | null;
}

interface VendorBillsResponse {
  vendor: {
    id: string;
    displayName: string;
    companyName?: string;
  };
  bills: Bill[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface EligibleInvoice {
  id: string;
  docNumber: string;
  txnDate?: string;
  customerId?: string;
  customerName?: string;
  totalAmount: number;
  balance: number;
  currency?: string;
  status: string;
}

interface VendorFinancialSummary {
  outstandingInvoicesByCurrency?: Array<{
    currencyCode: string;
    totalOutstanding: number;
    invoiceCount: number;
  }>;
  pendingPaymentsByCurrency?: Array<{
    currencyCode: string;
    totalPending: number;
    billCount: number;
  }>;
}

export const VendorBillsPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { hasPermission } = useUserPermissions();

  const [data, setData] = useState<VendorBillsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showOpenOnly, setShowOpenOnly] = useState(true);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [voidingBusiness, setVoidingBusiness] = useState(false);

  // Financial summary state
  const [financialSummary, setFinancialSummary] = useState<VendorFinancialSummary | null>(null);

  // Barrett QuickBooks integration state
  const [barrettConnected, setBarrettConnected] = useState(false);
  const [showBarrettActions, setShowBarrettActions] = useState(false);

  // Associate Bill to Invoice state
  const [associateModalOpen, setAssociateModalOpen] = useState(false);
  const [selectedBillForAssociation, setSelectedBillForAssociation] = useState<Bill | null>(null);
  const [eligibleInvoices, setEligibleInvoices] = useState<EligibleInvoice[]>([]);
  const [loadingEligibleInvoices, setLoadingEligibleInvoices] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('');
  const [associatingBill, setAssociatingBill] = useState(false);

  // Check if this is the Barrett vendor
  const isBarrettVendor = data?.vendor?.displayName === 'Barrett HTL Inc';

  const fetchVendorBills = async () => {
    if (!vendorId) return;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });
      if (showOpenOnly) {
        params.append('openOnly', 'true');
      }
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendor-bills/${vendorId}?${params.toString()}`
      );

      if (!response || !response.ok) {
        if (response?.status === 403) {
          throw new Error('Access denied. You do not have permission to view this vendor\'s bills.');
        }
        if (response?.status === 404) {
          throw new Error('Vendor not found');
        }
        throw new Error('Failed to fetch vendor bills');
      }

      const result = await response.json();
      setData(result.data);
    } catch (err: any) {
      console.error('Error fetching vendor bills:', err);
      setError(err.message || 'Failed to fetch vendor bills');
    } finally {
      setLoading(false);
    }
  };

  // Fetch vendor details for financial summary
  const fetchVendorFinancialSummary = async () => {
    if (!vendorId) return;

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/${vendorId}`
      );

      if (response && response.ok) {
        const result = await response.json();
        if (result.data?.financial) {
          setFinancialSummary({
            outstandingInvoicesByCurrency: result.data.financial.outstandingInvoicesByCurrency,
            pendingPaymentsByCurrency: result.data.financial.pendingPaymentsByCurrency,
          });
        }
      }
    } catch (err) {
      console.error('Error fetching vendor financial summary:', err);
    }
  };

  useEffect(() => {
    fetchVendorBills();
    fetchVendorFinancialSummary();
  }, [vendorId, page, rowsPerPage, showOpenOnly]);

  // Check Barrett connection status when vendor is Barrett
  const checkBarrettConnection = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/barrett-quickbooks/status`);
      if (response && response.ok) {
        const result = await response.json();
        setBarrettConnected(result.success && result.connected);
      } else {
        setBarrettConnected(false);
      }
    } catch (err) {
      console.error('Error checking Barrett connection:', err);
      setBarrettConnected(false);
    }
  };

  useEffect(() => {
    if (isBarrettVendor) {
      checkBarrettConnection();
    }
  }, [isBarrettVendor]);

  // Handler for opening Associate Bill to Invoice modal
  const handleOpenAssociateModal = async (bill: Bill) => {
    setSelectedBillForAssociation(bill);
    setAssociateModalOpen(true);
    setSelectedInvoiceId('');
    setLoadingEligibleInvoices(true);
    setEligibleInvoices([]);

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/eligible-invoices-for-bill/${bill.id}`
      );

      if (response?.ok) {
        const result = await response.json();
        setEligibleInvoices(result.data.eligibleInvoices || []);
      } else {
        const errorData = await response?.json();
        throw new Error(errorData?.message || 'Failed to fetch eligible invoices');
      }
    } catch (err: any) {
      console.error('Error fetching eligible invoices:', err);
      alert(err.message || 'Failed to fetch eligible invoices');
    } finally {
      setLoadingEligibleInvoices(false);
    }
  };

  // Handler for closing the Associate modal
  const handleCloseAssociateModal = () => {
    setAssociateModalOpen(false);
    setSelectedBillForAssociation(null);
    setEligibleInvoices([]);
    setSelectedInvoiceId('');
  };

  // Handler for associating bill to invoice
  const handleAssociateBillToInvoice = async () => {
    if (!selectedBillForAssociation || !selectedInvoiceId || !vendorId) return;

    setAssociatingBill(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/associate-bill-to-invoice`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            billId: selectedBillForAssociation.id,
            invoiceId: selectedInvoiceId,
            vendorId: vendorId,
          }),
        }
      );

      if (response?.ok) {
        // Refresh the bills list to show the new association
        await fetchVendorBills();
        handleCloseAssociateModal();
      } else {
        const errorData = await response?.json();
        throw new Error(errorData?.message || 'Failed to associate bill to invoice');
      }
    } catch (err: any) {
      console.error('Error associating bill to invoice:', err);
      alert(err.message || 'Failed to associate bill to invoice');
    } finally {
      setAssociatingBill(false);
    }
  };

  const formatCurrency = (amount?: number, currency: string = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleVoidBusiness = async () => {
    if (!selectedBill || !vendorId) return;

    setVoidingBusiness(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/${vendorId}/void-business`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            invoiceId: selectedBill.invoice?.id,
            billId: selectedBill.id
          })
        }
      );

      if (!response || !response.ok) {
        const errorData = await response?.json();
        console.error('QuickBooks void error:', errorData);

        // Format detailed error message
        let errorMessage = errorData?.message || 'Failed to void business';

        if (errorData?.details && Array.isArray(errorData.details)) {
          const detailedErrors = errorData.details
            .map((detail: any) => `${detail.message || ''} ${detail.detail || ''}`.trim())
            .filter((msg: string) => msg)
            .join('\n');

          if (detailedErrors) {
            errorMessage += '\n\nDetails:\n' + detailedErrors;
          }
        }

        throw new Error(errorMessage);
      }

      setVoidModalOpen(false);
      setSelectedBill(null);
      // Refresh the bills list
      await fetchVendorBills();
    } catch (err: any) {
      console.error('Error voiding business:', err);
      alert(err.message || 'Failed to void business');
    } finally {
      setVoidingBusiness(false);
    }
  };

  const handleDownloadSubsidiaryInvoice = async (subsidiaryInvoiceId: number) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/subsidiary-invoices/${subsidiaryInvoiceId}/document`
      );

      if (!response || !response.ok) {
        throw new Error('Failed to get document URL');
      }

      const result = await response.json();
      if (result.success && result.data?.url) {
        // Open the SAS URL in a new tab to trigger download
        window.open(result.data.url, '_blank');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err: any) {
      console.error('Error downloading subsidiary invoice:', err);
      alert(err.message || 'Failed to download subsidiary invoice');
    }
  };

  if (loading && !data) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">
          No bills data available.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        {hasPermission('accounting.admin') && (
          <Link
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/hub/accounting/vendors')}
          >
            Vendors
          </Link>
        )}
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/admin/hub/accounting/vendors/${vendorId}`)}
        >
          {data.vendor.displayName}
        </Link>
        <Typography color="text.primary">Business</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => navigate(`/admin/hub/accounting/vendors/${vendorId}`)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <ReceiptIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                {data.vendor.displayName} - Business
              </Typography>
              {data.vendor.companyName && data.vendor.companyName !== data.vendor.displayName && (
                <Typography variant="body1" color="text.secondary">
                  {data.vendor.companyName}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/admin/hub/accounting/vendors/${vendorId}/business/new`)}
            >
              Add New Business
            </Button>
            <IconButton onClick={() => { fetchVendorBills(); fetchVendorFinancialSummary(); }} title="Refresh">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Financial Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Outstanding Invoices */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Outstanding Invoices
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {financialSummary?.outstandingInvoicesByCurrency && financialSummary.outstandingInvoicesByCurrency.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {financialSummary.outstandingInvoicesByCurrency.map((item) => (
                  <Box key={item.currencyCode} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {item.currencyCode} ({item.invoiceCount} invoice{item.invoiceCount !== 1 ? 's' : ''})
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: item.totalOutstanding > 0 ? 'success.main' : 'text.primary' }}>
                      {formatCurrency(item.totalOutstanding, item.currencyCode)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No outstanding invoices
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Pending Payments */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Pending Payments
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {financialSummary?.pendingPaymentsByCurrency && financialSummary.pendingPaymentsByCurrency.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {financialSummary.pendingPaymentsByCurrency.map((item) => (
                  <Box key={item.currencyCode} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {item.currencyCode} ({item.billCount} bill{item.billCount !== 1 ? 's' : ''})
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: item.totalPending > 0 ? 'success.main' : 'text.primary' }}>
                      {formatCurrency(item.totalPending, item.currencyCode)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No pending payments
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Bills Table */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', gap: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showOpenOnly}
                onChange={(e) => {
                  setShowOpenOnly(e.target.checked);
                  setPage(0); // Reset to first page when filter changes
                }}
              />
            }
            label="Show only open business"
          />
          {isBarrettVendor && barrettConnected && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showBarrettActions}
                  onChange={(e) => setShowBarrettActions(e.target.checked)}
                />
              }
              label="Show Barrett QBOL actions"
            />
          )}
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>HHTL Invoice #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Invoice Amount</TableCell>
                <TableCell align="right">Invoice Balance</TableCell>
                <TableCell align="center" sx={{ width: 40 }}></TableCell>
                <TableCell>HHTL Bill #</TableCell>
                <TableCell>Subsidiary Inv#</TableCell>
                <TableCell align="right">Bill Amount</TableCell>
                <TableCell align="right">Bill Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Notes</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.bills.map((bill) => (
                <TableRow key={bill.id}>
                  <TableCell>
                    {bill.invoice?.docNumber ? (
                      <Chip
                        label={bill.invoice.docNumber}
                        size="small"
                        clickable
                        onClick={() => navigate(`/admin/hub/accounting/invoices/${bill.invoice?.id}`)}
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: 'rgba(0, 45, 96, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 45, 96, 0.15)',
                          }
                        }}
                      />
                    ) : (
                      <Typography variant="body2">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {bill.invoice?.transactionDate ? formatDate(bill.invoice.transactionDate) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {bill.invoice?.customerId && bill.invoice?.customerName ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/admin/hub/accounting/customers/${bill.invoice?.customerId}`)}
                        sx={{ textAlign: 'left' }}
                      >
                        {bill.invoice.customerName}
                      </Link>
                    ) : (
                      <Typography variant="body2">
                        {bill.invoice?.customerName || '—'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {bill.invoice?.totalAmount !== undefined ?
                        formatCurrency(bill.invoice.totalAmount, bill.invoice.currencyCode) :
                        '—'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {bill.invoice?.balance !== undefined ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: bill.invoice.balance === 0 ? 'green' :
                                 bill.invoice.balance > 0 ? 'error.main' :
                                 'text.primary'
                        }}
                      >
                        {formatCurrency(bill.invoice.balance, bill.invoice.currencyCode)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ width: 40 }}>
                    {bill.invoice && (
                      <Tooltip title="Bill linked to Invoice">
                        <LinkIcon sx={{ fontSize: 18 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bill.id}
                      size="small"
                      clickable
                      onClick={() => navigate(`/admin/hub/accounting/bills/${bill.id}`)}
                      sx={{
                        fontFamily: 'monospace',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.15)',
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {bill.subsidiaryInvoiceNumber && bill.subsidiaryInvoiceId ? (
                      <Tooltip title={bill.subsidiaryInvoiceNumber}>
                        <Chip
                          label={bill.subsidiaryInvoiceNumber.length > 10
                            ? `${bill.subsidiaryInvoiceNumber.substring(0, 10)}...`
                            : bill.subsidiaryInvoiceNumber}
                          size="small"
                          clickable
                          onClick={() => handleDownloadSubsidiaryInvoice(bill.subsidiaryInvoiceId!)}
                          sx={{
                            fontFamily: 'monospace',
                            backgroundColor: 'rgba(76, 175, 80, 0.08)',
                            '&:hover': {
                              backgroundColor: 'rgba(76, 175, 80, 0.15)',
                            }
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(bill.totalAmount, bill.currencyCode)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        color: bill.balance === 0 ? 'green' :
                               bill.balance && bill.balance > 0 ? 'error.main' :
                               'text.primary'
                      }}
                    >
                      {formatCurrency(bill.balance, bill.currencyCode)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(bill.invoice?.balance === 0 && bill.balance !== 0 && bill.balance !== null && bill.balance !== undefined) ? (
                      <Chip
                        label="PAY BILL"
                        size="small"
                        color="error"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : bill.invoice?.status ? (
                      <Chip
                        label={bill.invoice.status.toUpperCase()}
                        size="small"
                        color={
                          bill.invoice.status === 'paid' ? 'success' :
                          bill.invoice.status === 'void' ? 'default' :
                          'warning'
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {bill.invoice?.hasNotes && <NoteIcon hasNotes={true} />}
                      {bill.hasNotes && <NoteIcon hasNotes={true} />}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {isBarrettVendor && barrettConnected && showBarrettActions && !bill.invoice && (
                        <Tooltip title="Associate Bill to Invoice">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenAssociateModal(bill)}
                            >
                              <AddLinkIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {bill.invoice?.status === 'open' && bill.invoice?.source === 'portal' && (
                        <Tooltip title="Void Invoice and Bill">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedBill(bill);
                              setVoidModalOpen(true);
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {data.bills.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      {showOpenOnly ? 'No open business found' : 'No business found for this vendor'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={data.pagination.total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Void Business Confirmation Modal */}
      <Dialog open={voidModalOpen} onClose={() => !voidingBusiness && setVoidModalOpen(false)}>
        <DialogTitle>Void Invoice and Bill?</DialogTitle>
        <DialogContent>
          <Typography>
            This action will VOID both the open invoice and the bill and cannot be undone.
          </Typography>
          {selectedBill && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Invoice:</strong> {selectedBill.invoice?.docNumber || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Bill:</strong> {selectedBill.docNumber || selectedBill.id}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidModalOpen(false)} disabled={voidingBusiness}>
            Cancel
          </Button>
          <Button
            onClick={handleVoidBusiness}
            color="error"
            variant="contained"
            disabled={voidingBusiness}
          >
            {voidingBusiness ? 'Voiding...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Associate Bill to Invoice Modal */}
      <Dialog
        open={associateModalOpen}
        onClose={() => !associatingBill && handleCloseAssociateModal()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Associate Bill to Invoice</DialogTitle>
        <DialogContent>
          {selectedBillForAssociation && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Bill ID:</strong> {selectedBillForAssociation.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Bill Amount:</strong> {formatCurrency(selectedBillForAssociation.totalAmount, selectedBillForAssociation.currencyCode)}
              </Typography>
            </Box>
          )}

          {loadingEligibleInvoices ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : eligibleInvoices.length === 0 ? (
            <Alert severity="info">
              No eligible invoices found. Invoices must have an amount greater than or equal to the bill amount and not already be associated with another bill.
            </Alert>
          ) : (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="invoice-select-label">Select Invoice</InputLabel>
              <Select
                labelId="invoice-select-label"
                value={selectedInvoiceId}
                label="Select Invoice"
                onChange={(e) => setSelectedInvoiceId(e.target.value)}
              >
                {eligibleInvoices.map((invoice) => (
                  <MenuItem key={invoice.id} value={invoice.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">
                        {invoice.docNumber} - {invoice.customerName || 'Unknown Customer'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(invoice.totalAmount, invoice.currency)} | {invoice.status.toUpperCase()}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAssociateModal}
            disabled={associatingBill}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssociateBillToInvoice}
            color="primary"
            variant="contained"
            disabled={associatingBill || !selectedInvoiceId || loadingEligibleInvoices}
          >
            {associatingBill ? 'Associating...' : 'Associate Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};